import { Container, Graphics, LINE_JOIN, Text } from "pixi.js"

import { TextRenderer } from "../text/text_renderer"
import {
    CHAT_CORNER,
    CHAT_PAD_X,
    CHAT_PAD_Y,
    CHAT_WIDTH,
    LOG_FONT_SIZE,
    LOG_LINE_GAP,
    LOG_OLDEST_ALPHA,
    LOG_PANEL_ALPHA,
    PANEL_FILL,
    RIM_IDLE,
    SHADOW_ALPHA,
    SHADOW_COLOR,
    SHADOW_OFFSET,
    TEXT_COLOR,
    speakerColor,
} from "./chat_theme"

export interface ChatLogEntry {
    // Absent for a line nobody said -- a system notice routed through the log.
    speaker?: string
    text: string
    self?: boolean
}

// How much of a row's width the speaker's name may take before it is given its own line. Past
// this, the hanging indent leaves the message in a gutter too narrow to read.
const MAX_NAME_SHARE = 0.45
// Air between the name and the message that follows it on the same line.
const NAME_GAP = 5
// Used only where a Text measures as zero (a headless/mocked canvas), so the panel can never
// collapse into a sliver with every line drawn on top of the last.
const FALLBACK_LINE_H = LOG_FONT_SIZE + 3

// The chat log: the last few lines anyone said, bottom-left, above the chat input.
//
// It is a glance, not a transcript -- it floats over the world uninvited and fades itself out --
// so it is built to be READ in that glance: the speaker's name carries a stable colour of its own
// (your own lines are gold), the message hangs in a column beside the name rather than running on
// after it, and older lines are dimmer than newer ones.
//
// The panel sizes itself to its content and hangs from a fixed BOTTOM edge (`pos`, in parent
// coordinates), so it grows upward into empty grass and never moves the chat input underneath it.
export class ChatHistory extends Container {
    pos: number
    private shadow: Graphics
    private panel: Graphics
    private rows: Container
    // The row built for each entry currently on screen, so an arriving line costs one row rather
    // than a whole log's worth of text rasterization.
    private rowCache: Map<ChatLogEntry, Container> = new Map()

    constructor(pos: number) {
        super()

        this.pos = pos
        this.shadow = new Graphics()
        this.panel = new Graphics()
        this.rows = new Container()
        this.rows.x = CHAT_PAD_X
        this.rows.y = CHAT_PAD_Y
        this.addChild(this.shadow)
        this.addChild(this.panel)
        this.addChild(this.rows)
    }

    hide = () => {
        this.visible = false
    }

    show = () => {
        this.visible = true
    }

    // Entries oldest-first; the newest is drawn at the bottom, nearest the input.
    updateEntries = (entries: ChatLogEntry[]) => {
        const shown = entries.filter(entry => !!entry.text)
        if (shown.length === 0) {
            this.clearRows()
            this.shadow.clear()
            this.panel.clear()
            return
        }

        // The log is append-only: every call but the first adds ONE line and drops at most one off
        // the top. Rebuilding all of it would re-rasterize a dozen Texts in the frame a message
        // lands in, which on an integrated GPU is a visible hitch for nothing (see the budget
        // hardware target), so rows are kept and looked up by the entry they were built from.
        const kept = new Map<ChatLogEntry, Container>()
        let y = 0

        shown.forEach((entry, index) => {
            const row = this.rowCache.get(entry) || this.buildRow(entry)
            kept.set(entry, row)
            row.y = y
            // Recency as opacity: full strength for the newest line, fading back through the ones
            // the player has probably already read.
            row.alpha =
                shown.length === 1
                    ? 1
                    : LOG_OLDEST_ALPHA + ((1 - LOG_OLDEST_ALPHA) * index) / (shown.length - 1)
            if (row.parent !== this.rows) {
                this.rows.addChild(row)
            }
            y += Math.max(row.height || FALLBACK_LINE_H, FALLBACK_LINE_H) + LOG_LINE_GAP
        })

        this.rowCache.forEach((row, entry) => {
            if (!kept.has(entry)) {
                this.rows.removeChild(row)
                row.destroy({ children: true })
            }
        })
        this.rowCache = kept

        const height = Math.round(y - LOG_LINE_GAP + CHAT_PAD_Y * 2)
        this.draw(height)
        this.y = this.pos - height
    }

    // One line: the speaker's name, then the message in a column beside it, wrapped to whatever
    // width that leaves.
    private buildRow = (entry: ChatLogEntry): Container => {
        const inner = CHAT_WIDTH - CHAT_PAD_X * 2
        const row = new Container()
        let bodyX = 0
        let bodyY = 0

        if (entry.speaker) {
            const name = new TextRenderer()
                .fontSize(LOG_FONT_SIZE)
                .color(speakerColor(entry.speaker, entry.self))
                .bold()
                .strokeThickness(0)
                .render(entry.speaker) as Text
            row.addChild(name)

            const nameWidth = name.width || entry.speaker.length * (LOG_FONT_SIZE * 0.6)
            if (nameWidth + NAME_GAP <= inner * MAX_NAME_SHARE) {
                bodyX = nameWidth + NAME_GAP
            } else {
                // A long name takes the line to itself and the message wraps full width under it,
                // rather than being squeezed into what is left.
                bodyY = (name.height || FALLBACK_LINE_H) + 1
            }
        }

        const body = new TextRenderer()
            .fontSize(LOG_FONT_SIZE)
            .color(TEXT_COLOR)
            .strokeThickness(0)
            .wordWrap(Math.max(40, inner - bodyX))
            .render(entry.text) as Text
        body.x = bodyX
        body.y = bodyY
        row.addChild(body)

        return row
    }

    private draw = (height: number) => {
        this.shadow.clear()
        this.shadow.beginFill(SHADOW_COLOR, SHADOW_ALPHA)
        this.shadow.drawRoundedRect(SHADOW_OFFSET, SHADOW_OFFSET, CHAT_WIDTH, height, CHAT_CORNER)
        this.shadow.endFill()

        this.panel.clear()
        this.panel.lineStyle({ width: 1, color: RIM_IDLE, alpha: 0.45, join: LINE_JOIN.ROUND })
        this.panel.beginFill(PANEL_FILL, LOG_PANEL_ALPHA)
        this.panel.drawRoundedRect(0, 0, CHAT_WIDTH, height, CHAT_CORNER)
        this.panel.endFill()
    }

    private clearRows = () => {
        this.rows.removeChildren().forEach(child => child.destroy({ children: true }))
        this.rowCache = new Map()
    }
}

// "Bryn: the fire is low" -> { speaker: "Bryn", text: "the fire is low" }, so a caller that still
// hands over one formatted line gets a coloured name like everyone else. A colon far into the line
// is punctuation in somebody's sentence, not a speaker, so only a short leading one counts.
const MAX_PARSED_NAME = 24

export const entryFromLine = (line: string): ChatLogEntry => {
    const at = line.indexOf(": ")
    if (at <= 0 || at > MAX_PARSED_NAME) {
        return { text: line }
    }
    return { speaker: line.slice(0, at), text: line.slice(at + 2) }
}
